import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faUser } from "@fortawesome/pro-solid-svg-icons";
import { StatusCode } from "api/protocols";
import { IBooking, ITeeTime } from "redux/reducers/models/teetime";
import { convertTime, valueToString } from "helpers/Helpers";
import Spin from "components/spin/spin";
import { ButtonNew as Button } from "components/buttonNew";
import { useHistory } from "react-router";
import { Badge } from "components/badge/Badge";
import { useTranslation } from "react-i18next";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import "./clientPortalBookingList.scss";
import Callout from "components/callout/Callout";
import { GetBookingParticipant } from "api/rpc/2024-04/customer/booking";
import { useClientPortal } from "../useClientPortal";
import Tabs from "components/tabs/Tabs";
import ReactDOM from "react-dom";
import Search from "components/search/Search";
import DropFilter from "components/dropdown/DropFilter";
import { CustomerDeleteLotteryRequest, CustomerGetLotteryRequest, CustomerPutLotteryRequest, ICustomerLotteryRequest, IPutLotteryRequestParams } from "api/rpc/2024-04/customer/lotteryRequest";
import CustomerLotteryRequestModal from "../Home/CustomerLotteryRequestModal/CustomerLotteryRequestModal";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import { ILottery } from "redux/reducers/models/lottery";
import RequestCard from "pages/guest/Portal/PortalBookings/requestCard/RequestCard";
import Popup from "components/popup/Popup";
import { CustomerGetLottery, CustomerGetLotteryTeeTimes } from "api/rpc/2024-04/customer/lottery";

interface IFilterState {
  limit: number;
  offset: number;
}

const BOOKING_PLAYERS_FILTER_KEY = "booking_players_filters";
const BOOKING_STATUS_FILTER_KEY = "booking_status_filters";
const BOOKING_DATE_FILTER_KEY = "booking_date_filters";

const REQUEST_TYPE_FILTER_KEY = "request_type_filters";

const REQUESTS_LIMIT = 15;


export default function ClientPortalBookingsList() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<IBooking[]>(null);
  const [filter, setFilter] = useState<IFilterState>({
    limit: 15,
    offset: 0,
  });
  const dispatch = useAppDispatch();
  const { clientPortalStore } = useAppSelector(store => store);
  const history = useHistory();
  const HOME_URL = useClientPortal().getHomePageUrl();
  const [tabsSelected, setTabState] = useState(0);
  const [requestFilter, setRequestFilter] = useState<string>("all_requests");
  const [searchValue, setSearchValue] = useState("");

  //Request state
  const [requestState, setRequestState] = useState<{ requests: Array<ICustomerLotteryRequest>; offset: number }>({ requests: [], offset: 0 });
  const {
    state: lotteryRequestModal,
    closeModal: closeLotteryRequestModal,
    updateModal: updateLotteryRequestModal,
  } = useModal<{ lottery: ILottery; lotteryTimes: Array<ITeeTime>; teetime: ITeeTime; request: ICustomerLotteryRequest }>({ lottery: null, lotteryTimes: [], teetime: null, request: null });
  const { state: cancelRequestPopup, updateModal: updateCancelRequestModal, closeModal: closeCancelRequestModal } = useModal<{ id: number }>({ id: null });

  // Filter states
  const [playersFilter, setPlayersFilter] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<string[]>([]);

  const bookingsTabs = [
    {
      content: t("All Bookings"),
    },
    {
      content: t("My Bookings"),
    },
    {
      content: t("Shared Bookings"),
    },
    {
      content: t("Requests"),
    },
  ];

   // Filter data definitions
   const playersFilterData = [
    { id: 1, label: "1 Player" },
    { id: 2, label: "2 Players" },
    { id: 3, label: "3 Players" },
    { id: 4, label: "4 Players" },
  ];

  const statusFilterData = [
    { id: "unchecked", label: t("Unchecked") },
    { id: "checked", label: t("Checked In") },
    { id: "no_show", label: t("No Show") },
    { id: "cancelled", label: t("Cancelled") },
  ];

  const dateFilterData = [
    { id: "upcoming", label: t("Upcoming Bookings") },
    { id: "past", label: t("Past Bookings") },
  ];

  const requestTypeFilterData = [
    { id: "all_requests", label: t("All Requests") },
    { id: "my_requests", label: t("My Requests") },
    { id: "shared_requests", label: t("Shared Requests") },
  ];
  useEffect(() => {
    const source = axios.CancelToken.source();
    if (clientPortalStore?.client) {
      if (tabsSelected < 3) {
        void getBookings(source.token);
      } else if (tabsSelected === 3) {
        void getRequests(source.token);
      }
      
    } else {
      history.push(HOME_URL);
    }

    return () => {
      source.cancel("Cancelled");
    };
  }, [filter, searchValue, tabsSelected, statusFilter, playersFilter, dateFilter, requestFilter, requestState?.offset]);

  async function getBookings(token: CancelToken) {
    //display loader
    if (bookings !== null) {
      setBookings(null);
    }
    const bookingsRes = await GetBookingParticipant(
      { ...filter, 
        client_id: clientPortalStore?.client?.id, 
        search: searchValue,
        extended_booking: true,
        guest: tabsSelected === 0 ? undefined : tabsSelected === 1 ? false : tabsSelected === 2 ? true : undefined,
        status: statusFilter, 
        player_count: playersFilter, 
        is_past: dateFilter.includes("past") ? true : dateFilter.includes("upcoming") ? false : undefined,
        },
      false,
      token,
    );

    if (bookingsRes.status !== StatusCode.OK) {
      if (token.reason) {
        return;
      }
      dispatch(showError(t("Error getting bookings")));
      setBookings([]);
      return;
    }
    setBookings(bookingsRes.data as Array<IBooking>);
  }

  async function getRequests(token?: CancelToken) {
    if (requestState?.requests !== undefined) {
      //loader
      setRequestState(prev => ({ ...prev, requests: undefined }));
    }

    const res = await CustomerGetLotteryRequest({ offset: requestState?.offset, limit: REQUESTS_LIMIT, guest: requestFilter === "my_requests" ? false : requestFilter === "shared_requests" ? true : undefined }, false, token);
    if (res?.status !== StatusCode.OK) {
      if (token && token?.reason) {
        return;
      }
      setRequestState(prev => ({ ...prev, requests: [] }));
      dispatch(showError(t("Error getting lottery requests")));
      return;
    }
    setRequestState(prev => ({ ...prev, requests: res?.data }));
  }

  function handleViewBooking(id: string) {
    if (id) {
      window.open(`/tee-on/portal/bookings/${id}/view`);
    }
  }

  function handlePlayersFilter(filterReturn: Record<string, any>[]) {
    setPlayersFilter(filterReturn.map(item => item.id as number));
  }

  function handleStatusFilter(filterReturn: Record<string, any>[]) {
    setStatusFilter(filterReturn.map(item => item.id as string));
  }

  function handleDateFilter(filterReturn: Record<string, any>[]) {
    setDateFilter(filterReturn.map(item => item.id as string));
  }

  function handleRequestsFilter(filterReturn: Record<string, any>[]) {
    if (filterReturn?.length > 0) {
      setRequestFilter(filterReturn[0]?.id as string);
    } else {
      setRequestFilter("all_requests");
    }
  }

  async function deleteRequest() {
    if (cancelRequestPopup?.id) {
      const res = await CustomerDeleteLotteryRequest({ id: cancelRequestPopup?.id }, true);
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error cancelling request")));
        return;
      }
      dispatch(showSuccess(t("Successfully cancelled request")));
      void closeCancelRequestModal();
      void getRequests();
    }
  }

  async function updateLotteryRequest(params: IPutLotteryRequestParams) {
    const res = await CustomerPutLotteryRequest(params, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t(typeof res?.data === "string" ? res?.data : "Error updating lottery request")));
      return;
    }
    dispatch(showSuccess(t("Successfully updated tee time lottery request")));
    void closeLotteryRequestModal();
    void getRequests();
  }

  async function getLotteryRequestDetails(facility_id: number, course_id: number, date: string, lottery_id: number, request: ICustomerLotteryRequest) {
    dispatch(enqueue());
    const [lotteryRes, teetimesRes] = await Promise.all([CustomerGetLottery({ id: lottery_id, facility_id: facility_id }, false), CustomerGetLotteryTeeTimes({
      date: date,
      facility_id: facility_id,
      course_id: course_id,
      lottery_id: lottery_id,
      turn_tee_time: true,
      extended: true,
    }, false)]);
    dispatch(dequeue());

    if (lotteryRes?.status !== StatusCode.OK) {
      dispatch(showError(typeof lotteryRes?.data === "string" ? lotteryRes?.data : t("Unable to modify this request")));
      return;
    }

    if (teetimesRes?.status !== StatusCode.OK || teetimesRes?.data?.length === 0) {
      dispatch(showError(typeof teetimesRes?.data === "string" ? teetimesRes?.data : t("Unable to modify this request")));
      return;
    }

    const teetimes = teetimesRes?.data;

    const currentTeeTime = teetimes?.find(teetime => teetime?.start_time === request?.start_time_preference_one);

    void updateLotteryRequestModal({ isOpen: true, lottery: lotteryRes?.data, lotteryTimes: teetimes, teetime: currentTeeTime, request: request });
  }

  return (
    <div className="client-portal-booking-list">
      <ClientPortalNavigation showNavOptions />
      <div className="client-portal-booking-list-container">
        <h1 className="bookings-header-text">{t('Bookings')}</h1>
        <Tabs tabs={bookingsTabs} selected={tabsSelected} onSelect={tabsSelected => {
        ReactDOM.unstable_batchedUpdates(() => {
          setTabState(tabsSelected);
          setRequestFilter("all_requests");
        });
      }} />
      {tabsSelected < 3 ? (
        <>
          <div className="search-container" style={{ marginBottom: '24px' }}>
          <Search
            searchCallback={searchValue => setSearchValue(searchValue)}
            historyKey="portal-bookings-search"
            placeholder={t("Search bookings...")}
            restrictPresetToRoutes={[undefined]}
          />
          </div>

          {/* Filter Section */}
          <div className="flex flex-row flex-wrap gap-4 mb-4">
            <DropFilter
              title={t("Players")}
              filterData={playersFilterData}
              filterLabelPropFromData="label"
              filterIdPropFromData="id"
              filterType="Checkbox"
              historyKey={BOOKING_PLAYERS_FILTER_KEY}
              applyFilters={handlePlayersFilter}
              save
              leftAligned
            />

            <DropFilter
              title={t("Status")}
              filterData={statusFilterData}
              filterLabelPropFromData="label"
              filterIdPropFromData="id"
              filterType="Checkbox"
              historyKey={BOOKING_STATUS_FILTER_KEY}
              applyFilters={handleStatusFilter}
              save
            />

            <DropFilter
              title={t("Date")}
              filterData={dateFilterData}
              filterLabelPropFromData="label"
              filterIdPropFromData="id"
              filterType="Checkbox"
              historyKey={BOOKING_DATE_FILTER_KEY}
              applyFilters={handleDateFilter}
              save
            />
          </div>

          {bookings ? (
          <div className="guest-bookings-body-container client-portal-booking-list-group">
         {bookings?.length > 0 ? (
            bookings?.map((booking, rowIndex) => {
              //If group bookings exist, total the number of players on each booking, otherwise use the quantity on the booking
              const totalPlayers = booking?.grouped_bookings && booking?.grouped_bookings?.length > 0 ? 
              //Total the quantity on each grouped booking then add the quantity on the main booking
              booking?.grouped_bookings?.reduce((quantity, booking) => quantity + booking?.quantity, 0) + booking?.booking?.quantity 
              : booking?.booking?.quantity;

              //Get the start time of the last group booking which is the "end time"
              const endTimeBooking = booking?.grouped_bookings && booking?.grouped_bookings?.length > 0 ? 
              booking?.grouped_bookings?.sort((a,b) => a?.tee_time?.start_time?.localeCompare(b?.tee_time?.start_time))[booking?.grouped_bookings?.length - 1]
              : null;
              const endTime = endTimeBooking ? endTimeBooking?.tee_time?.start_time : null;
              return (
                <div
                  key={rowIndex}
                  onClick={() => handleViewBooking(booking?.booking?.id as string)}
                  className={"guest-bookings-container booking-border"}
                >
                  <div className="guest-bookings-course">
                    {booking.tee_time ? <span>{booking.tee_time.course.long_name}</span> : <span>{t('N/A')}</span>}
                  </div>

                  <div className="guest-bookings-date-time-container">
                    <div className="guest-bookings-date-time">
                      {moment(booking.tee_time?.date).format("dddd, LL")}
                    </div>

                    <div className="guest-bookings-date-time">
                      {booking.tee_time ? <span>{convertTime(booking.tee_time?.start_time)} {endTime ? ` - ${convertTime(endTime)}` : null}</span> : <span>{t('N/A')}</span>}
                    </div>
                  </div>

                  <div className="guest-bookings-quantity-status-container">
                    <div className="guest-bookings-quantity">
                      <FontAwesomeIcon icon={faUser} />

                      <span>
                        {totalPlayers} {totalPlayers === 1 ? t("Player") : t("Players")}
                      </span>
                    </div>

                    <div className="guest-bookings-status">
                      {booking.status !== "cancelled" ? (
                        booking.check_in_status !== "unchecked" && (
                          <Badge size="small" type={returnBadgeType(booking.check_in_status as string)}>
                            {valueToString(booking.check_in_status as string)}
                          </Badge>
                        )
                      ) : (
                        <Badge size="small" type="error">
                          {valueToString(booking.booking.status as string)}
                        </Badge>
                      )}
                      {!!booking?.booking?.order_id && (
                        <Badge
                          type="success"
                          size="small"
                          iconSize="medium"
                          iconLeft={<FontAwesomeIcon icon={["far", "dollar-sign"]} />}
                        >
                          {t('Pre Paid')}
                        </Badge>
                      )}
                      {!!booking?.booking?.booking_fee_order_id && (
                        <Badge
                          type="success"
                          size="small"
                          iconSize="medium"
                          iconLeft={<FontAwesomeIcon icon={["far", "dollar-sign"]} />}
                        >
                          {t('Booking Fee Paid')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ): (
            <Callout
              type={"info"}
              title={t("No Bookings Found")}
              content={t("There are no bookings found under this user. Make a new booking to view it here.")}
            />
          )}
          </div>
        ) : (
          <span style={{ display: "flex", justifyContent: "center" }}>
            <Spin />
          </span>
        )}

        {bookings && bookings?.length > 0 && (
          <div className="bookings-footer">
            <div>
              <Button
                size="small"
                type="secondary"
                onClick={() => setFilter(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
                disabled={!bookings || bookings?.length === 0 || !(filter.limit <= filter.offset)}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                &nbsp;
                {t("Prev")}
              </Button>
            </div>
            <div>
              <Button
                size="small"
                type="secondary"
                onClick={() => setFilter(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={!bookings || bookings?.length === 0 || !(bookings?.length === filter.limit)}
              >
                {t("Next")}
                &nbsp;
                <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </div>
          </div>
        )}
        </>
      ) : (<>
        <div className="flex flex-row flex-wrap gap-4 mb-4 mt-4">
          <DropFilter
            title={t("Requests")}
            filterData={requestTypeFilterData}
            filterLabelPropFromData="label"
            filterIdPropFromData="id"
            filterType="Checkbox"
            historyKey={REQUEST_TYPE_FILTER_KEY}
            applyFilters={handleRequestsFilter}
            save
            leftAligned
            defaultCheckboxes={["all_requests"]}
            restrain
          />
        </div>
        {requestState?.requests ? (
          <div className="guest-bookings-body-container">
            {requestState?.requests?.length > 0 ? (
              requestState?.requests?.map((request, index) => {
                return (
                  <RequestCard
                    key={index}
                    request={request}
                    onCancelRequestClick={id => updateCancelRequestModal({ isOpen: true, id })}
                    onModifyClick={(facility_id, course_id, date, lottery_id) => getLotteryRequestDetails(facility_id, course_id, date, lottery_id, request)} />
                );
              })
            ) : (
              <Callout type="info" title={t("No Requests Found")} content={t("There are no requests made under this account")} />
            )}
          </div>
        ) : (
          <span style={{ display: "flex", justifyContent: "center" }}>
            <Spin />
          </span>
        )}
        <div className="guest-bookings-footer">
          <div>
            <Button
              size="small"
              type="secondary"
              onClick={() => setRequestState(prev => ({ ...prev, offset: prev.offset - REQUESTS_LIMIT }))}
              disabled={!requestState?.requests || !(REQUESTS_LIMIT <= requestState.offset)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              &nbsp;
              {t("Prev")}
            </Button>
          </div>
          <div>
            <Button
              size="small"
              type="secondary"
              onClick={() => setRequestState(prev => ({ ...prev, offset: prev.offset + REQUESTS_LIMIT }))}
              disabled={!(requestState?.requests?.length === REQUESTS_LIMIT)}
            >
              {t("Next")}
              &nbsp;
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </div>


        {/* Edit Lottery Request */}
        <CustomerLotteryRequestModal
          open={lotteryRequestModal?.isOpen}
          onCancel={closeLotteryRequestModal}
          onOk={updateLotteryRequest}
          facility={lotteryRequestModal?.teetime?.facility}
          lottery={lotteryRequestModal?.lottery}
          lotteryTimes={lotteryRequestModal?.lotteryTimes}
          teetime={lotteryRequestModal?.teetime}
          request={lotteryRequestModal?.request}
          title={t("Edit Lottery Request")}
        />

        {/* Cancel Lottery Request */}
        <Portal isMounted={cancelRequestPopup?.isOpen}>
          <Popup
            title={t("Cancel Request")}
            open={cancelRequestPopup?.isOpen}
            description={t("Are you sure you want to cancel this request? This cannot be undone.")}
            onCancel={closeCancelRequestModal}
            onOk={deleteRequest}
            type="warning" />
        </Portal>
      </>)}
      </div>
    </div>
  );
}

function returnBadgeType(checkInStatus: string) {
  switch (checkInStatus) {
    case "checked":
      return "public";
    case "no_show":
      return "error";
    default:
      return "gray";
  }
}
